'use client';

import Image from 'next/image';
import Link from 'next/link';
import { LazyMotion, domAnimation, m } from 'framer-motion';
import TextLinkButton from '@/components/ui/TextLinkButton';
import Underline from '@/components/ui/underline';
import type { PreparedBlogPost } from '@/lib/blog-list-vm';

type Props = {
    tagline: string;
    heading: string;
    description: string;
    viewAllHref: string;
    viewAllLabel?: string;
    blogPosts: PreparedBlogPost[];
};

const slideUp = {
    hidden: { y: 40, opacity: 0 },
    visible: (i: number) => ({
        y: 0,
        opacity: 1,
        transition: { duration: 0.8, ease: [0.215, 0.61, 0.355, 1], delay: i * 0.1 },
    }),
} as const;

export default function BlogSectionClient({
    tagline,
    heading,
    description,
    viewAllHref,
    viewAllLabel = 'ZOBACZ WSZYSTKIE',
    blogPosts,
}: Props) {
    return (
        <LazyMotion features={domAnimation}>
            <m.section
                id="blog"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.15 }}
                className="px-[5%] py-16 md:py-24 lg:py-28"
            >
                <div className="container mx-auto max-w-7xl">
                    <div className="mb-12 md:mb-18 lg:mb-20">
                        {tagline ? (
                            <div className="relative mb-10 inline-block md:mb-14 lg:mb-16">
                                <m.p variants={slideUp} custom={0} className="text-sm uppercase tracking-[0.10em]">
                                    {tagline}
                                </m.p>
                                <Underline
                                    delay={0.8}
                                    useWhileInView
                                    className="pointer-events-none absolute -left-2 top-full -mt-1 h-5 w-[120px] text-accent md:h-6 md:w-[150px]"
                                />
                            </div>
                        ) : null}
                        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between md:gap-10 lg:gap-16">
                            <div className="w-full max-w-lg">
                                <m.h2
                                    variants={slideUp}
                                    custom={1}
                                    className="mb-5 text-4xl font-medium leading-[1.1] md:mb-6 md:text-7xl"
                                >
                                    {heading}
                                </m.h2>
                                {description ? (
                                    <m.p variants={slideUp} custom={2} className="text-base leading-relaxed md:text-md">
                                        {description}
                                    </m.p>
                                ) : null}
                            </div>
                            <m.div variants={slideUp} custom={2} className="shrink-0 self-start md:self-end">
                                <TextLinkButton text={viewAllLabel} hoverColor="#803320" href={viewAllHref} />
                            </m.div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-x-8 gap-y-12 md:grid-cols-2 md:gap-y-16 lg:grid-cols-3">
                        {blogPosts.map((post, index) => (
                            <m.article
                                key={post.url}
                                variants={slideUp}
                                custom={index + 2}
                                className="group flex h-full flex-col overflow-hidden rounded-[40px] bg-primary-light pb-6 text-black"
                            >
                                <Link href={post.url} scroll className="block overflow-hidden p-2">
                                    <div className="relative aspect-[4/3] w-full overflow-hidden rounded-[32px] bg-primary-light">
                                        <Image
                                            src={post.imageSrc}
                                            alt={post.imageAlt}
                                            fill
                                            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                                            priority={index === 0}
                                            loading={index === 0 ? 'eager' : 'lazy'}
                                            fetchPriority={index === 0 ? 'high' : 'auto'}
                                            decoding="async"
                                            draggable={false}
                                            placeholder={post.blurDataURL ? 'blur' : 'empty'}
                                            blurDataURL={post.blurDataURL}
                                            className="object-cover object-top transition-transform duration-700 ease-out md:group-hover:scale-105"
                                        />
                                    </div>
                                </Link>

                                <div className="flex flex-1 flex-col justify-between px-5 pt-4 pr-6 md:px-6 md:pt-8 md:pr-7">
                                    <div>
                                        <Link href={post.url} scroll className="mb-4 block">
                                            <h3 className="text-md font-medium leading-tight md:text-2xl">{post.title}</h3>
                                        </Link>
                                    </div>

                                    <div className="mt-8 self-end mr-4">
                                        <TextLinkButton text="CZYTAJ" hoverColor="#803320" href={post.url} />
                                    </div>
                                </div>
                            </m.article>
                        ))}
                    </div>
                </div>
            </m.section>
        </LazyMotion>
    );
}
